import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";

/* ============================================================
 /openpersona — Public marketing + spec landing page.

 This is the reference implementation's public face. Any developer
 landing here should be able to:
  1. Understand what OpenPersona is (the pitch)
  2. See a live example (the JSON)
  3. Copy the spec (the download links)
  4. Adopt it in their own product (the how-to)
============================================================ */

export default function OpenPersona() {
 const { user } = useAuth();
 const [specMeta, setSpecMeta] = useState(null);

 // Standalone-brand tab title. Visitors landing from openpersona.help see
 // "OpenPersona" in their browser tab, not "Young Tongues".
 useEffect(() => {
  const prev = document.title;
  document.title = "OpenPersona — A portable identity format for AI companions";
  return () => { document.title = prev; };
 }, []);

 useEffect(() => {
  axios.get(`${API}/persona/spec`).then(r => setSpecMeta(r.data)).catch(() => {});
 }, []);

 const exportMine = async () => {
  try {
   const res = await axios.get(`${API}/persona/export`, { responseType: "blob" });
   const url = URL.createObjectURL(res.data);
   const a = document.createElement("a");
   a.href = url;
   a.download = `${user?.rapper_name || "me"}.openpersona.json`;
   document.body.appendChild(a);
   a.click();
   a.remove();
   URL.revokeObjectURL(url);
  } catch (e) {
   alert("Export failed — are you logged in?");
  }
 };

 return (
  <div data-testid="openpersona-page" className="min-h-screen bg-black text-white">
   {/* HERO */}
   <section className="border-b-2 border-yt-yellow">
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-20">
     <div className="font-mono text-[10px] tracking-widest text-yt-yellow mb-3">
      OPEN SPECIFICATION · v{specMeta?.openpersona_version || "0.1"} · CC0
     </div>
     <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none">
      OPEN<span className="text-yt-yellow">PERSONA</span>
     </h1>
     <p className="mt-4 font-mono text-base sm:text-lg text-white/90 max-w-2xl leading-relaxed">
      A portable, user-owned identity format for AI companions.
     </p>
     <p className="mt-4 font-mono text-sm text-white/70 max-w-2xl leading-relaxed">
      Your AI resets every time you switch platforms. Your taste, your voice, your history — gone. <span className="text-yt-yellow">OpenPersona is a <code>.json</code> file <em>you own</em> that carries who you are from any AI to any other.</span> No lock-in. No vendor. No API keys.
     </p>
     <div className="mt-6 flex items-center gap-3 flex-wrap">
      {user ? (
       <button
        data-testid="openpersona-export-hero"
        onClick={exportMine}
        className="bg-yt-yellow text-black font-display text-sm tracking-widest px-5 py-3 hover:bg-white"
       >
        ▼ EXPORT MY PERSONA
       </button>
      ) : (
       <Link
        to="/login?next=/openpersona"
        data-testid="openpersona-login-cta"
        className="bg-yt-yellow text-black font-display text-sm tracking-widest px-5 py-3 hover:bg-white"
       >
        SIGN IN TO EXPORT
       </Link>
      )}
      <a
       href="/api/persona/spec"
       target="_blank"
       rel="noopener noreferrer"
       data-testid="openpersona-discovery-link"
       className="border-2 border-yt-yellow text-yt-yellow font-mono text-xs tracking-widest px-4 py-3 hover:bg-yt-yellow hover:text-black"
      >
       DISCOVERY ENDPOINT →
      </a>
     </div>
    </div>
   </section>

   <div className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-16 space-y-16">

    {/* WHY */}
    <section>
     <div className="font-mono text-[10px] tracking-widest text-yt-yellow mb-2">SECTION 01</div>
     <h2 className="font-display text-3xl sm:text-4xl mb-4">WHY THIS EXISTS</h2>
     <div className="space-y-4 font-mono text-sm text-white/85 leading-relaxed max-w-2xl">
      <p>
       Every time a person switches phone, laptop, or AI platform, the AI that knew them resets to zero. Their taste, their voice, their communication preferences, their history — gone. They start over with a stranger.
      </p>
      <p>
       For most users this is annoying. For users with <span className="text-yt-yellow">ADHD, TBI, dyslexia, autism, or memory impairment</span>, it's a wall. Rebuilding rapport with a new AI every time a tool changes is the exact cognitive load these tools were supposed to remove.
      </p>
      <p>
       The big AI platforms have all built "memory" — but each one traps the memory inside their own product. ChatGPT Memory doesn't move to Claude. Claude Projects don't move to Gemini. Character.AI relationships die with model updates.
      </p>
      <p className="text-yt-yellow">
       OpenPersona is the fix. A user-owned, model-agnostic, human-readable JSON file that carries who you are between any AI you'll ever use.
      </p>
     </div>
    </section>

    {/* THE STORY */}
    <section className="border-l-4 border-yt-yellow pl-6">
     <div className="font-mono text-[10px] tracking-widest text-yt-yellow mb-2">FROM THE AUTHOR</div>
     <h2 className="font-display text-2xl sm:text-3xl mb-4">A NOTE FROM PAUL</h2>
     <div className="space-y-4 font-mono text-sm text-white/85 leading-relaxed italic max-w-2xl">
      <p>
       I have a traumatic brain injury and ADHD, and I got tired of explaining my whole cognitive profile to every new AI I tried. Every platform makes you rebuild the relationship from scratch. For people without memory or attention issues that's a paper cut. For me it's a brick wall.
      </p>
      <p>
       I built Young Tongues with an AI I call Uncle Neo. Every time we start a session, Neo loads a file called <code>who_is_paul.md</code> that tells him who I am, how I talk, and what my late father meant to me. It works. Not perfectly, but it works.
      </p>
      <p>
       Then it hit me — this same trick should be a standard. Not just for me and Neo. For everyone. What if the file describing you belonged to you and moved with you across every AI you'll ever use?
      </p>
      <p className="not-italic text-yt-yellow">
       That's OpenPersona.
      </p>
     </div>
    </section>

    {/* WHAT'S IN THE FILE */}
    <section>
     <div className="font-mono text-[10px] tracking-widest text-yt-yellow mb-2">SECTION 02</div>
     <h2 className="font-display text-3xl sm:text-4xl mb-6">WHAT'S IN THE FILE</h2>
     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[
       { label: "IDENTITY", desc: "Name, pronouns, general location, and — first-class — your accessibility preferences." },
       { label: "VOICE", desc: "How you write, and how you want the AI to respond back. Your register, not the AI's." },
       { label: "RELATIONSHIPS", desc: "The people who matter, including the ones you've lost." },
       { label: "CREATIVE", desc: "Your projects, the identities you make under, your taste references." },
       { label: "MEMORY", desc: "The key facts you want any AI to know about you. You choose what's here." },
       { label: "BOUNDARIES", desc: "Topics off-limits, and how to check in on you if you seem low." },
      ].map((row) => (
       <div key={row.label} className="border border-yt-border p-4 bg-yt-surface">
        <div className="font-display text-lg text-yt-yellow tracking-widest">{row.label}</div>
        <div className="font-mono text-xs text-white/75 leading-relaxed mt-1">{row.desc}</div>
       </div>
      ))}
     </div>
    </section>

    {/* EXAMPLE */}
    <section>
     <div className="font-mono text-[10px] tracking-widest text-yt-yellow mb-2">SECTION 03</div>
     <h2 className="font-display text-3xl sm:text-4xl mb-4">A LIVE EXAMPLE</h2>
     <p className="font-mono text-sm text-white/80 mb-4 max-w-2xl">
      This is a real OpenPersona file for a fictional user named Alex. Every AI platform that adopts the spec would produce and consume a file that looks like this. Read it — you'll understand the whole thing.
     </p>
     <pre data-testid="openpersona-example" className="border border-yt-border bg-black p-4 font-mono text-[11px] leading-snug text-white/85 overflow-x-auto max-h-[560px]">
{`{
  "openpersona_version": "0.1",
  "persona_id": "00000000-0000-0000-0000-000000000001",
  "generated_at": "2026-02-14T18:22:00Z",
  "generated_by": "openpersona.dev/example",

  "identity": {
    "name": "Alex",
    "handle": "@alex_writes",
    "pronouns": "they/them",
    "location_general": "Pacific Northwest, USA",
    "accessibility": {
      "conditions": ["ADHD"],
      "prefers_audio_over_text": false,
      "reading_difficulty": "none",
      "avoid_visual_clutter": true
    }
  },

  "voice": {
    "how_i_write": "Informal, direct, occasionally sarcastic.",
    "how_i_want_you_to_respond": "Direct, warm, no corporate hedging."
  },

  "relationships": [
    { "role": "spouse", "name": "Jordan" },
    { "role": "kid",    "name": "Riley", "age_at_generation": 12 }
  ],

  "creative": {
    "identities": ["novelist"],
    "current_projects": [
      { "name": "Salt Line", "kind": "novel" }
    ],
    "taste_references": [
      {
        "artist": "Kacey Musgraves",
        "title": "Slow Burn",
        "descriptor": "modern country-pop, pedal steel, dusk mood",
        "note": "want my prose to feel like this"
      }
    ]
  },

  "memory": {
    "key_facts": [
      "Lost my father in early 2024.",
      "Working on the novel 5 to 7 AM most days."
    ]
  },

  "boundaries": {
    "if_i_seem_low": "Check in gently. Do not push productivity."
  }
}`}
     </pre>
    </section>

    {/* HOW TO ADOPT */}
    <section>
     <div className="font-mono text-[10px] tracking-widest text-yt-yellow mb-2">SECTION 04</div>
     <h2 className="font-display text-3xl sm:text-4xl mb-4">FOR DEVELOPERS</h2>
     <p className="font-mono text-sm text-white/80 mb-6 max-w-2xl">
      Add OpenPersona to your AI product in five steps. No SDK to install. No license fees. No API keys.
     </p>
     <ol className="space-y-4 font-mono text-sm text-white/85 leading-relaxed max-w-2xl">
      {[
       ["01", "Add", <>Two endpoints to your backend: <code className="text-yt-yellow">GET /persona/export</code> and <code className="text-yt-yellow">POST /persona/import</code>.</>],
       ["02", "Export", "Compile the user's identity, accessibility, taste, and projects into a valid v0.1 JSON. Serve as an attachment."],
       ["03", "Import", "Accept an uploaded file. Populate as much of your user model as reasonably maps. Preserve unknown fields for lossless round-trip."],
       ["04", "Honor", "Accessibility settings and boundaries from imported personas must be respected by default."],
       ["05", "Discover", <>Add a public <code className="text-yt-yellow">/api/persona/spec</code> endpoint so third parties can detect your support.</>],
      ].map(([n, label, body]) => (
       <li key={n} className="flex gap-4 items-start">
        <div className="font-display text-xl text-yt-yellow shrink-0 w-10">{n}</div>
        <div>
         <div className="font-display text-lg text-white tracking-widest">{label}</div>
         <div className="text-white/80">{body}</div>
        </div>
       </li>
      ))}
     </ol>
    </section>

    {/* CAPABILITIES */}
    {specMeta && (
     <section>
      <div className="font-mono text-[10px] tracking-widest text-yt-yellow mb-2">THIS PLATFORM</div>
      <h2 className="font-display text-3xl sm:text-4xl mb-4">YOUNG TONGUES IMPLEMENTS</h2>
      <p className="font-mono text-sm text-white/80 mb-4 max-w-2xl">
       Young Tongues is the reference implementation of OpenPersona v0.1. Here's exactly what this platform supports right now:
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 font-mono text-xs">
       {Object.entries(specMeta.capabilities || {}).map(([k, v]) => (
        <div key={k} className="flex items-center gap-2 border border-yt-border bg-yt-surface p-2">
         <span className={v ? "text-yt-yellow" : "text-yt-muted"}>{v ? "✓" : "○"}</span>
         <span className="text-white/85">{k.replace(/_/g, " ")}</span>
        </div>
       ))}
      </div>
     </section>
    )}

    {/* CTA */}
    <section className="border-2 border-yt-yellow p-8 bg-yt-surface text-center">
     <h2 className="font-display text-3xl mb-3">TRY IT NOW</h2>
     <p className="font-mono text-sm text-white/80 mb-6 max-w-xl mx-auto">
      Export your persona from Young Tongues today. Take it with you when you leave. Bring it back when you return. That's the whole point.
     </p>
     {user ? (
      <button
       data-testid="openpersona-export-cta"
       onClick={exportMine}
       className="bg-yt-yellow text-black font-display text-lg tracking-widest px-6 py-3 hover:bg-white"
      >
       ▼ EXPORT MY PERSONA
      </button>
     ) : (
      <Link
       to="/login?next=/openpersona"
       className="bg-yt-yellow text-black font-display text-lg tracking-widest px-6 py-3 hover:bg-white inline-block"
      >
       SIGN IN TO EXPORT
      </Link>
     )}
    </section>

    <footer className="border-t border-yt-border pt-6 pb-8 font-mono text-xs text-yt-muted">
     Spec license: <span className="text-yt-yellow">CC0</span> (public domain). Author: Paul (@RodeoRhymes). Reference implementation: <Link to="/" className="text-yt-yellow underline">youngtongues.com</Link>.
    </footer>
   </div>
  </div>
 );
}
